import { EAssetType, SaveAction } from '~containers/EditorPageContainer/types';
import { IClientClip, IClientClipCreate, IClientThumbnail, IClipComponent } from '~services/clip/types';
import { ILiveStreamCdnInfo, StreamType } from '~services/live/types';
import { IEditorStreamState, IStreamUrl } from '~src/store/editor/stream/types';

function filterStreamUrls(streamUrls: IStreamUrl[], drmFilter: boolean = null, typeFilter: StreamType = null): IStreamUrl[] {
    return streamUrls.filter((streamUrl: IStreamUrl) => {
        if (drmFilter !== null && streamUrl.drm !== drmFilter) {
            return false;
        }

        if (typeFilter !== null && streamUrl.type !== typeFilter) {
            return false;
        }

        return true;
    });
}

function isCombinedClip(components: IClipComponent[]) {
    return components.length > 1;
}

function isCreateClip(saveAction: SaveAction, clientClip: IClientClip): clientClip is IClientClipCreate {
    return saveAction === SaveAction.CREATE;
}

function isCreateLiveClip(saveAction: SaveAction, clientClip: IClientClip, assetType: EAssetType) {
    return isCreateClip(saveAction, clientClip) && assetType === EAssetType.LIVE;
}

const clientClipFactory = (
    title: string,
    components: IClipComponent[],
    thumbnail: IClientThumbnail,
    hlsStreamUrl: string,
    dashStreamUrl: string,
    streamUrls: IEditorStreamState['streamUrls'],
    saveAction: SaveAction,
    assetType: EAssetType,
    streamingConfigId: number,
    cdnInfo: ILiveStreamCdnInfo
): IClientClip => {
    const { origin, pathname }: URL = new URL(hlsStreamUrl);

    const clientClip: IClientClip = {
        hlsStreamUrl: origin + pathname,
        components,
    };

    if (dashStreamUrl) {
        const { origin: dashOrigin, pathname: dashPathname }: URL = new URL(dashStreamUrl);
        clientClip.dashStreamUrl = dashOrigin + dashPathname;
    }

    if (isCreateClip(saveAction, clientClip)) {
        clientClip.videoName = title;
        if (thumbnail) {
            clientClip.thumbnail = thumbnail;
        }
    }

    if (isCreateLiveClip(saveAction, clientClip, assetType)) {
        // Live Clips now use the v4 API, which now uses streamUrls.
        delete clientClip.hlsStreamUrl;
        delete clientClip.dashStreamUrl;
        // Stream URLs should not have any auth tokens applied
        let sanitizedStreamUrls = streamUrls.map((streamUrl: IStreamUrl) => {
            const queryStringIndex = streamUrl.url.indexOf('?');
            const url = queryStringIndex === -1 ? streamUrl.url : streamUrl.url.substr(0, queryStringIndex);

            return {
                ...streamUrl,
                url,
            };
        });

        const liveStreamHasDrm = sanitizedStreamUrls.some((stream: IStreamUrl) => stream.drm);

        /*
          If the live stream is non-DRM:
              send all non-DRM streams for single clips (ie. both HLS + DASH if DASH is available)
              send just the HLS non-DRM stream for combined clips
          If the live stream is DRM:
              send all DRM streams + HLS non-DRM stream for single clips
              send just the HLS non-DRM stream for combined clips
         */

        if (isCombinedClip(components)) {
            // send just the HLS non-DRM stream for combined clips
            sanitizedStreamUrls = filterStreamUrls(sanitizedStreamUrls, false, 'HLS');
        } else if (!liveStreamHasDrm) {
            // send all non-DRM streams for single clips (ie. both HLS + DASH if DASH is available)
            sanitizedStreamUrls = filterStreamUrls(sanitizedStreamUrls, false);
        } else {
            // send all DRM streams + HLS non-DRM stream for single clips
            // i.e. send all streams
        }

        clientClip.requestStreams = sanitizedStreamUrls;
        clientClip.cdnInfo = cdnInfo;

        if (streamingConfigId) {
            clientClip.streamingConfigId = streamingConfigId;
        }
    }

    return clientClip;
};

export { clientClipFactory, isCreateClip };
